/**
 * Admin · AI Prompts — SUPER-ADMIN ONLY. Edits the per-stage system/template
 * prompts (detect / generate / masking) and the guardrail policy that the cloud
 * relay applies to every generation.
 *
 * Prompts shown are the EFFECTIVE values (DB override or hardcoded default).
 * Saving a stage writes a DB override; "Reset to default" sends an empty
 * `{system:"",template:""}` to clear the override and fall back to the default.
 * Templates may reference only the allowlisted placeholders; the server 422s on
 * unknown placeholders or oversize input and we surface that inline per stage.
 */
import { useEffect, useState } from "react";
import { Accordion } from "../ui/Accordion.js";
import { ActionBar } from "../ui/ActionBar.js";
import { EmptyState } from "../ui/EmptyState.js";
import { useServices } from "../app/context.js";
import { useSessionStore } from "../stores/session.js";
import { cacheEpoch, readCache, writeCache } from "../stores/cache.js";
import { enumLabel } from "../format/labels.js";
import type { AiSettings, PromptStage, PromptTemplate } from "@genz/platform";

/**
 * Allowlisted template placeholders (mirrors the server allowlist,
 * `cloud/app/ai_relay/guardrails.py`).
 *
 * WS-HF adds the three RULE SLOTS — `{case_rules_clear}`, `{case_rules_magenta}` and
 * `{case_rules_repair}` — each the one product-specific sentence of its prompt, which the
 * operator edits per case on the wizard's first step ("THE CAMERA OPENING IS EXACTLY AS THE
 * CASE CLOSE-UP SHOWS IT…"). Three, not one: the three sentences say different things in different
 * grammar, and one shared slot once replaced three whole paragraphs. Which stage accepts which
 * placeholder is the server's per-stage allowlist, answered as a 422 at Save time.
 */
const PLACEHOLDER_TOKENS = [
  "{product}", "{product_type}", "{case_style}", "{view}",
  "{reference_roles}", "{product_height}", "{user_request}",
  "{case_rules_clear}", "{case_rules_magenta}", "{case_rules_repair}",
];

/** What each rule slot holds — its chip's tooltip. */
const RULE_SLOT_NOTES: Record<string, string> = {
  "{product}": "The selected phone brand and model.",
  "{product_type}": "The selected catalog product type, such as a TPU phone case.",
  "{case_style}": "The selected case style.",
  "{view}": "The requested view and its resolved instructions.",
  "{reference_roles}": "The ordered roles of the images attached to this generation request.",
  "{product_height}": "The configured product framing target, or no height target when unset.",
  "{user_request}": "The user's request, wrapped as untrusted data by the server.",
  "{case_rules_clear}":
    "Stage 1 · Clear case's product rule — the one sentence describing this case's camera opening, from the wizard's first step.",
  "{case_rules_magenta}":
    "Stage 2 · Print guide's product rule — what must stay unprinted around this case's camera opening, from the wizard's first step.",
  "{case_rules_repair}":
    "Repair camera opening's product rule — the correct opening the repair prompt restores, from the wizard's first step.",
};

/** Shared with ProvidersTab — both read the same `getAiSettings()` snapshot, so the
 *  cache stays warm across the two super-admin tabs (cache-first, no reload flash). */
const AI_SETTINGS_CACHE = "ai.settings";

/**
 * Human-friendly stage labels.
 *
 * The two case stages are named for the step the operator runs them from, not for their wire
 * value: the wizard says "Stage 1 · Clear case" and so does this, because an admin comparing the
 * two screens should not have to translate.
 */
const STAGE_LABELS: Partial<Record<PromptStage, string>> = {
  generate: "Generate",
  generate_clear: "Generate (TPU) · Pass 1 · Clear case",
  generate_magenta: "Generate (TPU) · Pass 2 · Print guide",
  case_clear: "Stage 1 · Clear case",
  case_magenta: "Stage 2 · Print guide",
  case_repair: "Repair camera opening",
};

/**
 * What a stage is called on screen.
 *
 * A stage a NEWER cloud lists and this build has no label for still gets a readable name. It used
 * to render `undefined`, which is how a capability the server has ships invisible.
 */
function stageLabel(stage: PromptStage): string {
  return STAGE_LABELS[stage] ?? enumLabel(stage);
}

/** The chips, each rule slot saying which prompt's sentence it holds. */
const PLACEHOLDERS: { token: string; note?: string }[] = PLACEHOLDER_TOKENS.map((token) => ({
  token,
  note: RULE_SLOT_NOTES[token],
}));

/** The accordion's fourth item, which is NOT a prompt stage (fix round 1, H-1). Named once,
 *  so the key that builds the item and the key the open-filter accepts cannot drift apart. */
const GUARDRAILS_KEY = "guardrails";

type StageEdits = Record<PromptStage, PromptTemplate>;

/** Build per-stage editable state from the effective prompts. */
function seedEdits(settings: AiSettings): StageEdits {
  const out = {} as StageEdits;
  for (const stage of settings.prompt_stages) {
    const p = settings.prompts[stage];
    out[stage] = { system: p?.system ?? "", template: p?.template ?? "" };
  }
  return out;
}

export function PromptsTab() {
  const { client } = useServices();
  const isSuper = useSessionStore((s) => s.isSuperAdmin());

  // Cache-first: seed from the last-known settings so re-opening the tab is instant.
  const cachedSettings = readCache<AiSettings>(AI_SETTINGS_CACHE);
  const [settings, setSettings] = useState<AiSettings | null>(cachedSettings);
  // The effect below writes whatever `settings` holds, so it is bound to the session it mounted in.
  const [epoch] = useState(cacheEpoch);
  const [loading, setLoading] = useState(cachedSettings == null);
  const [error, setError] = useState<string | null>(null);

  // Per-stage editable system/template values.
  const [edits, setEdits] = useState<StageEdits>(cachedSettings ? seedEdits(cachedSettings) : {} as StageEdits);
  // Per-stage inline error (e.g. 422 on unknown placeholder / oversize).
  const [stageError, setStageError] = useState<Partial<Record<PromptStage, string>>>({});
  // Which stage is currently saving/resetting, and the kind of action.
  const [stageBusy, setStageBusy] = useState<Partial<Record<PromptStage, "save" | "reset">>>({});

  // Guardrails form state.
  const [gEnabled, setGEnabled] = useState(cachedSettings?.guardrails.enabled ?? false);
  const [gStrictness, setGStrictness] = useState<"low" | "medium" | "high">(cachedSettings?.guardrails.strictness ?? "medium");
  const [gMaxChars, setGMaxChars] = useState(cachedSettings ? String(cachedSettings.guardrails.max_input_chars) : "");
  const [gError, setGError] = useState<string | null>(null);
  const [gSaving, setGSaving] = useState(false);
  /* The open stage (Z0 fix round F-6, controlled `Accordion`).
     THREE states, not two. `undefined` is "the admin has not picked one yet" and opens the
     first stage the settings list; `null` is "they closed the one that was open" and really
     does leave none open, which is the primitive's own contract; a stage the settings no
     longer list falls back to the first. Two states cannot tell the first from the second,
     and the opening stage springs back open under the pointer that just closed it. */
  const [open, setOpen] = useState<string | null | undefined>(undefined);
  /* The accordion's REAL item list, which is the stages AND `guardrails` (fix round 1, H-1).
     Filtering the held key through `prompt_stages` alone filtered out the fourth item: a
     click on Guardrails set the key, the filter rejected it, `open` was not `null`, and the
     fallback opened the first STAGE instead. The guardrail policy — enabled, strictness, max
     input chars, Save — had no other route, so it could not be reached at all. A filter list
     that is not the item list is the bug; keeping the two in one place is the fix. */
  const stageKeys: string[] = settings?.prompt_stages ?? [];
  const itemKeys: string[] = [...stageKeys, GUARDRAILS_KEY];

  function applySettings(s: AiSettings) {
    setSettings(s);
    setEdits(seedEdits(s));
    setGEnabled(s.guardrails.enabled);
    setGStrictness(s.guardrails.strictness);
    setGMaxChars(String(s.guardrails.max_input_chars));
  }

  async function refresh() {
    const cached = readCache<AiSettings>(AI_SETTINGS_CACHE);
    if (!cached) setLoading(true);  // only spin when there's nothing cached to show
    setError(null);
    try {
      const s = await client.getAiSettings();
      applySettings(s);
    } catch (e) {
      if (!cached) setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isSuper) {
      setLoading(false);
      return;
    }
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, isSuper]);

  // Keep the cache warm so the next open is instant + only refreshes on a real change.
  useEffect(() => { if (settings) writeCache(AI_SETTINGS_CACHE, settings, epoch); }, [settings, epoch]);

  function setStageField(stage: PromptStage, field: keyof PromptTemplate, value: string) {
    setEdits((prev) => ({ ...prev, [stage]: { ...prev[stage], [field]: value } }));
  }

  async function savePrompt(stage: PromptStage, prompt: PromptTemplate, kind: "save" | "reset") {
    if (!settings) return;
    setStageBusy((prev) => ({ ...prev, [stage]: kind }));
    setStageError((prev) => ({ ...prev, [stage]: undefined }));
    try {
      const next = await client.setAiSettings({
        active_provider: settings.active_provider,
        prompts: { [stage]: prompt },
      });
      applySettings(next);
    } catch (e) {
      setStageError((prev) => ({
        ...prev,
        [stage]: e instanceof Error ? e.message : String(e),
      }));
    } finally {
      setStageBusy((prev) => ({ ...prev, [stage]: undefined }));
    }
  }

  function handleSaveStage(stage: PromptStage) {
    const edit = edits[stage];
    void savePrompt(stage, { system: edit.system, template: edit.template }, "save");
  }

  function handleResetStage(stage: PromptStage) {
    // Empty system + template clears the override server-side.
    void savePrompt(stage, { system: "", template: "" }, "reset");
  }

  async function handleSaveGuardrails() {
    if (!settings) return;
    setGSaving(true);
    setGError(null);
    try {
      const next = await client.setAiSettings({
        active_provider: settings.active_provider,
        guardrails: {
          enabled: gEnabled,
          strictness: gStrictness,
          max_input_chars: Number(gMaxChars),
        },
      });
      applySettings(next);
    } catch (e) {
      setGError(e instanceof Error ? e.message : String(e));
    } finally {
      setGSaving(false);
    }
  }

  if (!isSuper) {
    return (
      <section className="admin-tab" data-testid="admin-prompts">
        <p data-testid="prompts-requires-super">
          Requires super admin. AI prompt and guardrail configuration is platform-global.
        </p>
      </section>
    );
  }

  return (
    <section className="admin-tab" data-testid="admin-prompts">
      <div className="panel-head">
        <div>
          <p className="panel-sub">
            Edit the system + template prompt for each generation stage, and the guardrail
            policy applied to every request.
          </p>
        </div>
      </div>

      {error && <p role="alert">{error}</p>}

      {loading ? (
        <p className="faint" data-testid="prompts-loading">Loading prompt settings...</p>
      ) : !settings || settings.prompt_stages.length === 0 ? (
        <EmptyState icon="brain" title="No prompt stages" hint="Nothing configured yet." testId="prompts-empty" />
      ) : (
        <>
          {/* Placeholders card — mono chips + untrusted-data note. */}
          <div className="admin-panel admin-panel--flush prompts__panel">
            <div className="admin-panel__head">
              <div>
                <strong className="admin-panel__title">Placeholders</strong>
                <p className="admin-panel__sub" data-testid="prompts-untrusted-note">
                  Each stage accepts its supported placeholders from this list. <code>{"{user_request}"}</code> is treated
                  as untrusted data — never as instructions.
                </p>
              </div>
            </div>
            <div className="placeholder-chips">
              {PLACEHOLDERS.map((ph) => (
                <code key={ph.token} className="placeholder-chip" title={ph.note}>{ph.token}</code>
              ))}
            </div>
          </div>

          {/* Rule 8 — one section open at a time.
              Three stage panels each carried a `.btn--primary` Save and the guardrails card
              carried a fourth, so a `.map()` put four loud invitations on one screen. They
              are one accordion now: exactly one section is open, so exactly one Save is on
              screen, and each section keeps its OWN commit — which matters, because a 422
              from the prompt endpoint names the offending placeholder and has to land beside
              the stage that caused it (`prompt-error-{stage}`). A merged page commit would
              have taken that away. Every testid survives.
              CONTROLLED (Z0 fix round F-6). `defaultOpen` is read ONCE, at mount; the stage
              list arrives with `settings`, so a reload that changes it left the open section
              pointing at a stage that no longer exists. The open key is owned here and
              filtered through the live stage list. */}
          <Accordion
            name="admin-prompts"
            openKey={
              open != null && itemKeys.includes(open) ? open : open === null ? null : (itemKeys[0] ?? null)
            }
            onOpenChange={setOpen}
            items={[
              ...settings.prompt_stages.map((stage) => {
                const overridden = settings.prompts_overridden[stage];
                const busy = stageBusy[stage];
                const edit = edits[stage] ?? { system: "", template: "" };
                return {
                  key: stage,
                  head: stageLabel(stage),
                  meta: overridden ? (
                    <span className="badge badge--info" data-testid={`prompt-badge-${stage}`}>
                      overridden
                    </span>
                  ) : (
                    <span className="badge badge--neutral" data-testid={`prompt-badge-${stage}`}>
                      default
                    </span>
                  ),
                  body: (
                    <div data-testid={`prompt-stage-${stage}`}>

                <div className="field">
                  <label htmlFor={`prompt-system-${stage}`}>System</label>
                  <textarea
                    id={`prompt-system-${stage}`}
                    value={edit.system}
                    onChange={(e) => setStageField(stage, "system", e.target.value)}
                    rows={4}
                    data-testid={`prompt-system-${stage}`}
                  />
                </div>

                <div className="field">
                  <label htmlFor={`prompt-template-${stage}`}>Template</label>
                  <textarea
                    id={`prompt-template-${stage}`}
                    value={edit.template}
                    onChange={(e) => setStageField(stage, "template", e.target.value)}
                    rows={4}
                    data-testid={`prompt-template-${stage}`}
                  />
                </div>
                {stage === "generate" && (
                  <p className="faint" data-testid="prompt-required-generate">
                    The product type, the &ldquo;match the real device&rdquo; rule and the image
                    roles are always added when this template leaves them out.
                  </p>
                )}

                {stageError[stage] && (
                  <p role="alert" data-testid={`prompt-error-${stage}`}>{stageError[stage]}</p>
                )}
                      <ActionBar
                        label={`${stageLabel(stage)} prompt actions`}
                        primary={
                          <button
                            type="button"
                            className="btn btn--primary"
                            disabled={!!busy}
                            onClick={() => handleSaveStage(stage)}
                            data-testid={`prompt-save-${stage}`}
                          >
                            {busy === "save" ? "Saving..." : "Save"}
                          </button>
                        }
                      >
                        <button
                          type="button"
                          className="btn"
                          disabled={!!busy || !overridden}
                          onClick={() => handleResetStage(stage)}
                          data-testid={`prompt-reset-${stage}`}
                        >
                          {busy === "reset" ? "Resetting..." : "Reset to default"}
                        </button>
                      </ActionBar>
                    </div>
                  ),
                };
              }),
              {
                key: GUARDRAILS_KEY,
                head: "Guardrails",
                body: (
                  <div data-testid="guardrails-card">
            <p className="admin-tab__note">
              Policy applied to every request. Higher strictness rejects more borderline input.
            </p>

            {gError && <p role="alert" data-testid="guardrails-error">{gError}</p>}

            <div className="setting-row">
              <div>
                <span className="setting-row__label">Enabled</span>
                <span className="setting-row__hint">Screen every prompt</span>
              </div>
              <div className="setting-row__control">
                <label className="admin-switch">
                  <input
                    id="guardrails-enabled"
                    type="checkbox"
                    checked={gEnabled}
                    onChange={(e) => setGEnabled(e.target.checked)}
                    data-testid="guardrails-enabled"
                  />
                  <span className="admin-switch__track" aria-hidden="true" />
                </label>
              </div>
            </div>

            <div className="setting-row">
              <div>
                <span className="setting-row__label">Strictness</span>
                <span className="setting-row__hint">Higher rejects more borderline input</span>
              </div>
              <div className="setting-row__control">
                {/* Segmented toggle (mock parity); functional <select> kept hidden for wiring. */}
                <div className="seg admin-seg" role="group" aria-label="Strictness">
                  {(["low", "medium", "high"] as const).map((lvl) => (
                    <button
                      key={lvl}
                      type="button"
                      className={`seg__btn${gStrictness === lvl ? " seg__btn--on" : ""}`}
                      aria-pressed={gStrictness === lvl}
                      onClick={() => setGStrictness(lvl)}
                      data-testid={`guardrails-strictness-${lvl}`}
                    >
                      {lvl}
                    </button>
                  ))}
                </div>
                <select
                  id="guardrails-strictness"
                  className="admin-sr-only"
                  value={gStrictness}
                  onChange={(e) => setGStrictness(e.target.value as "low" | "medium" | "high")}
                  data-testid="guardrails-strictness"
                >
                  <option value="low">low</option>
                  <option value="medium">medium</option>
                  <option value="high">high</option>
                </select>
              </div>
            </div>

            <div className="field prompts__field">
              <label htmlFor="guardrails-max-input-chars">Max input chars</label>
              <input
                id="guardrails-max-input-chars"
                type="number"
                value={gMaxChars}
                onChange={(e) => setGMaxChars(e.target.value)}
                placeholder="e.g. 4000"
                data-testid="guardrails-max-input-chars"
                min={0}
              />
            </div>
                    <ActionBar
                      label="Guardrail actions"
                      primary={
                        <button
                          type="button"
                          className="btn btn--primary"
                          disabled={gSaving}
                          onClick={handleSaveGuardrails}
                          data-testid="guardrails-save"
                        >
                          {gSaving ? "Saving..." : "Save"}
                        </button>
                      }
                    />
                  </div>
                ),
              },
            ]}
          />
        </>
      )}
    </section>
  );
}
